<template>
  <EChartWrapper
    v-if="!loading"
    ref="chart"
    :options="lineChartOptions"
    :height="info.height"
    @data-point-click="(params: ECElementEvent) => emit('dataPointClick', params)"
  ></EChartWrapper>
  <ChartLoading v-else :height="info.height"></ChartLoading>
</template>

<script setup lang="ts">
import type { LineChartInfo } from "@/classes/ChartInfo";
import { getChartColors, getFormattedData } from "@/services/ChartsServices";
import { formatCurrency } from "@/services/Utility";
import { usePropsStore } from "@/stores/props";
import type { ECElementEvent } from "echarts";

interface Props {
  info: LineChartInfo;
}

const props = defineProps<Props>();
const propsStore = usePropsStore();
const loading = ref(true);
const chart = ref();

const showLoading = (): void => {
  loading.value = true;
};

const loadingComplete = (): void => {
  nextTick(() => {
    loading.value = false;
  });
};

defineExpose({ showLoading, loadingComplete });

const emit = defineEmits<{
  dataPointClick: [params: ECElementEvent];
}>();

const colors = ref<Array<Array<string>>>(getChartColors());
watch(
  () => propsStore.theme,
  (theme) => {
    colors.value = getChartColors();
  }
);
const data = computed(() => {
  return props.info.lines.map((value: any, index: number) => {
    return getFormattedData(value, colors.value, index);
  });
});

const lineChartOptions = computed<any>(() => {
  return {
    title: {
      text: props.info.title,
      subtext: props.info.subtitle,
    },
    tooltip: {
      valueFormatter: (value: any) => (props.info.currency ? formatCurrency(value) : value),
    },
    xAxis: {
      type: "category",
      data: props.info.labels,
      axisLabel: {
        rotate: props.info.xaxisRotate,
      },
    },
    yAxis: {
      type: "value",
      minInterval: props.info.minInterval,
      boundaryGap: [0, "15%"],
      axisLabel: {
        formatter: (value: any, index: number) => (props.info.currency ? formatCurrency(value) : value),
      },
    },
    legend: {
      show: props.info.showLegend,
    },
    series: data,
  };
});
</script>
